import type {
  CheckConstraintModel,
  ColumnModel,
  ForeignKeyModel,
  IndexModel,
  PrimaryKeyModel,
  SchemaModel,
  TableModel,
  UniqueConstraintModel,
} from '@bits2schema/shared';

/**
 * Čisté funkce nad `SchemaModel` sdílené napříč `schema/commands.ts` a UI
 * handlery. Klíčová komplikace: `PrimaryKeyModel.columns`,
 * `UniqueConstraintModel.columns`, `IndexModel.columns` a
 * `ForeignKeyModel.columns`/`referencedColumns` odkazují na sloupce
 * **jménem, ne ID** (`model.ts`) — přejmenování sloupce/tabulky proto musí
 * kaskádovat všude, kde se to jméno objevuje, jinak by reference tiše
 * osiřela. Diff engine (`diff.ts`) se schválně NEUPRAVUJE (epic-4 kap. 4 —
 * jeho rename-awareness patří až Epic 9).
 */

export function updateTable(
  schema: SchemaModel,
  tableId: string,
  updater: (table: TableModel) => TableModel,
): SchemaModel {
  return { ...schema, tables: schema.tables.map((table) => (table.id === tableId ? updater(table) : table)) };
}

/** Nastaví/zruší `renamedFrom` — `undefined` odstraní pole úplně (ne jen na `undefined`). */
function applyRenamedFrom<T extends { renamedFrom?: string }>(entity: T, renamedFrom: string | undefined): T {
  if (renamedFrom === undefined) {
    const { renamedFrom: _drop, ...rest } = entity;
    return rest as T;
  }
  return { ...entity, renamedFrom };
}

/**
 * Přejmenuje tabulku a promítne nový název do `referencedTable` všech FK
 * napříč schématem (včetně self-referenční FK té samé tabulky), aby žádná
 * vazba neosiřela (E4-US2 AC). `renamedFrom` nastavuje volající (viz
 * `schema/commands.ts` — drží se přes commandy, aby `revert()` obnovil
 * přesně předchozí hodnotu, ne jen `undefined`).
 */
export function renameTableCascade(
  schema: SchemaModel,
  tableId: string,
  newName: string,
  renamedFrom: string | undefined,
): SchemaModel {
  const target = schema.tables.find((table) => table.id === tableId);
  if (!target) return schema;
  const oldName = target.name;
  const oldSchema = target.schema;

  return {
    ...schema,
    tables: schema.tables.map((table) => {
      const withForeignKeys: TableModel = {
        ...table,
        foreignKeys: table.foreignKeys.map((fk) =>
          fk.referencedSchema === oldSchema && fk.referencedTable === oldName
            ? { ...fk, referencedTable: newName }
            : fk,
        ),
      };
      if (table.id !== tableId) return withForeignKeys;
      return applyRenamedFrom({ ...withForeignKeys, name: newName }, renamedFrom);
    }),
  };
}

/**
 * Přejmenuje sloupec a kaskáduje do PK/unique/index/zdrojových FK sloupců
 * téže tabulky a do `referencedColumns` každé FK napříč schématem, která na
 * tento sloupec cílí (E4-US5 AC — "zdrojový i cílový"). `newName` je vždy
 * hodnota PO změně; pro `revert()` volající zavolá tutéž funkci s
 * prohozeným směrem (rename je symetrický).
 */
export function renameColumnCascade(
  schema: SchemaModel,
  tableId: string,
  columnId: string,
  newName: string,
  renamedFrom: string | undefined,
): SchemaModel {
  const target = schema.tables.find((table) => table.id === tableId);
  const column = target?.columns.find((candidate) => candidate.id === columnId);
  if (!target || !column) return schema;
  const oldName = column.name;
  const replace = (columns: string[]) => columns.map((name) => (name === oldName ? newName : name));

  return {
    ...schema,
    tables: schema.tables.map((table) => {
      const withReferencedColumns: TableModel = {
        ...table,
        foreignKeys: table.foreignKeys.map((fk) =>
          fk.referencedSchema === target.schema && fk.referencedTable === target.name
            ? { ...fk, referencedColumns: replace(fk.referencedColumns) }
            : fk,
        ),
      };
      if (table.id !== tableId) return withReferencedColumns;

      return {
        ...withReferencedColumns,
        columns: table.columns.map((candidate) =>
          candidate.id === columnId ? applyRenamedFrom({ ...candidate, name: newName }, renamedFrom) : candidate,
        ),
        primaryKey: table.primaryKey
          ? { ...table.primaryKey, columns: replace(table.primaryKey.columns) }
          : null,
        uniqueConstraints: table.uniqueConstraints.map((unique) => ({ ...unique, columns: replace(unique.columns) })),
        indexes: table.indexes.map((index) => ({ ...index, columns: replace(index.columns) })),
        foreignKeys: withReferencedColumns.foreignKeys.map((fk) =>
          fk.columns.includes(oldName) ? { ...fk, columns: replace(fk.columns) } : fk,
        ),
      };
    }),
  };
}

/**
 * Odstraní jméno sloupce ze všech constraintů/indexů TÉŽE tabulky, které ho
 * obsahují (PK, unique, index) — nutné pro E4-US6 (mazání sloupce), aby po
 * smazání nezůstala visící reference na neexistující sloupec. FK blokaci
 * (zdrojová i cílová role, E4-US6 AC) řeší UI PŘED vytvořením commandu —
 * touto funkcí tedy nikdy neprojde sloupec, který je součástí nějaké FK.
 * Constraint/index, kterému by smazáním sloupce nezbyl žádný sloupec, se
 * odebere celý (jednosloupcový unique/index na mazaném sloupci nemá bez něj
 * smysl); PK ztrácející svůj jediný sloupec se nuluje na `null` (E4-US6 AC —
 * "tabulka zůstane bez PK", jde jen o UI upozornění, ne blok).
 */
export function removeColumnFromConstraints(table: TableModel, columnName: string): TableModel {
  const primaryKey: PrimaryKeyModel | null =
    table.primaryKey && table.primaryKey.columns.includes(columnName)
      ? table.primaryKey.columns.length > 1
        ? { ...table.primaryKey, columns: table.primaryKey.columns.filter((name) => name !== columnName) }
        : null
      : table.primaryKey;

  const uniqueConstraints: UniqueConstraintModel[] = table.uniqueConstraints
    .map((unique) => ({ ...unique, columns: unique.columns.filter((name) => name !== columnName) }))
    .filter((unique) => unique.columns.length > 0);

  const indexes: IndexModel[] = table.indexes
    .map((index) => ({ ...index, columns: index.columns.filter((name) => name !== columnName) }))
    .filter((index) => index.columns.length > 0 || index.definition !== null);

  return { ...table, primaryKey, uniqueConstraints, indexes };
}

/**
 * Epic 15, E15-US5 — prohodí sloupec se sousedním (v aktuálním pořadí podle
 * `ordinal`). No-op na okraji seznamu (`columnId` na první pozici + `'up'`,
 * na poslední + `'down'`) i pro neexistující `columnId` — volající (Inspector
 * tlačítka) navíc hraniční případy deaktivuje, tohle je jen obranná pojistka.
 *
 * Zásadní detail: `table.columns` POLE se přeuspořádá STEJNĚ jako `ordinal`,
 * ne jen číslo samotné — `canvas/geometry.ts#visibleColumns` (tedy i
 * `TableNode.tsx` a kotvení FK hran) čte sloupce v POŘADÍ POLE, zatímco
 * inspektor/nav strom/DDL/export explicitně řadí podle `ordinal`. Beze
 * shodného přeuspořádání pole by canvas ukazoval jiné pořadí než zbytek UI
 * (`doc/epic-15-rozsireni-projektu.md` kap. 8).
 */
export function moveColumn(table: TableModel, columnId: string, direction: 'up' | 'down'): TableModel {
  const sorted = [...table.columns].sort((a, b) => a.ordinal - b.ordinal);
  const index = sorted.findIndex((column) => column.id === columnId);
  const neighborIndex = direction === 'up' ? index - 1 : index + 1;
  if (index === -1 || neighborIndex < 0 || neighborIndex >= sorted.length) return table;

  const current = sorted[index];
  const neighbor = sorted[neighborIndex];
  sorted[index] = { ...neighbor, ordinal: current.ordinal };
  sorted[neighborIndex] = { ...current, ordinal: neighbor.ordinal };

  return { ...table, columns: sorted };
}

export interface ForeignKeyDependent {
  table: TableModel;
  foreignKey: ForeignKeyModel;
  role: 'source' | 'target';
}

/**
 * Najde FK vazby, které závisí na dané tabulce — jako zdroj (FK patří téhle
 * tabulce) i jako cíl (jiná tabulka na ni odkazuje). Používá UI k blokaci
 * mazání tabulky (E4-US3 AC) a k vypsání seznamu v potvrzovacím dialogu.
 */
export function findTableDependents(schema: SchemaModel, tableId: string): ForeignKeyDependent[] {
  const target = schema.tables.find((table) => table.id === tableId);
  if (!target) return [];
  const dependents: ForeignKeyDependent[] = [];
  for (const table of schema.tables) {
    if (table.id === tableId) {
      for (const foreignKey of table.foreignKeys) dependents.push({ table, foreignKey, role: 'source' });
      continue;
    }
    for (const foreignKey of table.foreignKeys) {
      if (foreignKey.referencedSchema === target.schema && foreignKey.referencedTable === target.name) {
        dependents.push({ table, foreignKey, role: 'target' });
      }
    }
  }
  return dependents;
}

/**
 * Najde FK vazby závislé na konkrétním sloupci — jako zdrojový i cílový
 * sloupec (E4-US6 AC "zdrojový nebo cílový"). Používá UI k blokaci mazání
 * sloupce.
 */
export function findColumnDependents(schema: SchemaModel, tableId: string, columnId: string): ForeignKeyDependent[] {
  const table = schema.tables.find((candidate) => candidate.id === tableId);
  const column = table?.columns.find((candidate) => candidate.id === columnId);
  if (!table || !column) return [];
  const dependents: ForeignKeyDependent[] = [];
  for (const foreignKey of table.foreignKeys) {
    if (foreignKey.columns.includes(column.name)) dependents.push({ table, foreignKey, role: 'source' });
  }
  for (const candidate of schema.tables) {
    for (const foreignKey of candidate.foreignKeys) {
      if (
        foreignKey.referencedSchema === table.schema &&
        foreignKey.referencedTable === table.name &&
        foreignKey.referencedColumns.includes(column.name)
      ) {
        dependents.push({ table: candidate, foreignKey, role: 'target' });
      }
    }
  }
  return dependents;
}

/** Přidá číselnou příponu, dokud jméno nekoliduje se sadou existujících. */
export function ensureUniqueName(base: string, existingNames: Iterable<string>): string {
  const taken = new Set(existingNames);
  if (!taken.has(base)) return base;
  let counter = 2;
  while (taken.has(`${base}_${counter}`)) counter += 1;
  return `${base}_${counter}`;
}

export function nextTableName(schema: SchemaModel, dbSchema: string): string {
  const taken = new Set(
    schema.tables.filter((table) => table.schema === dbSchema).map((table) => table.name),
  );
  let counter = 1;
  while (taken.has(`table_${counter}`)) counter += 1;
  return `table_${counter}`;
}

export function nextColumnName(table: TableModel): string {
  const taken = new Set(table.columns.map((column) => column.name));
  let counter = 1;
  while (taken.has(`column_${counter}`)) counter += 1;
  return `column_${counter}`;
}

export function defaultUniqueConstraintName(table: TableModel, columns: string[]): string {
  const existing = new Set(table.uniqueConstraints.map((unique) => unique.name));
  return ensureUniqueName(`${table.name}_${columns.join('_')}_key`, existing);
}

export function defaultCheckConstraintName(table: TableModel): string {
  const existing = new Set(table.checkConstraints.map((check) => check.name));
  return ensureUniqueName(`${table.name}_check`, existing);
}

export function defaultForeignKeyName(table: TableModel, columns: string[]): string {
  const existing = new Set(table.foreignKeys.map((fk) => fk.name));
  return ensureUniqueName(`${table.name}_${columns.join('_')}_fkey`, existing);
}

export function defaultIndexName(table: TableModel, columns: string[]): string {
  const existing = new Set(table.indexes.map((index) => index.name));
  return ensureUniqueName(`${table.name}_${columns.join('_')}_idx`, existing);
}

/**
 * Typová kompatibilita FK páru — sdílená mezi formulářem (`ForeignKeyDialog`)
 * a vizuálním drag&drop editorem (Epic 5), aby se pravidla nikdy nerozešla
 * (epic-5 kap. 8). Stejná přesná shoda `canonicalType ?? rawType`, jakou dřív
 * dialog počítal inline.
 */
export function areForeignKeyColumnTypesCompatible(source: ColumnModel, target: ColumnModel): boolean {
  return (source.canonicalType ?? source.rawType) === (target.canonicalType ?? target.rawType);
}

/**
 * Je daná sada sloupců (jménem) přesně PK, nebo přesně některý unique
 * constraint tabulky? Nezávazné upozornění „cíl FK není v PK/unique"
 * (epic-4 kap. 7, E4-US8 AC) — sdíleno stejně jako typová kompatibilita výše.
 */
export function isColumnSetKeyed(table: TableModel, columnNames: string[]): boolean {
  const columnSet = new Set(columnNames);
  const pkMatches =
    table.primaryKey !== null &&
    columnSet.size === table.primaryKey.columns.length &&
    table.primaryKey.columns.every((columnName) => columnSet.has(columnName));
  const uniqueMatches = table.uniqueConstraints.some(
    (unique) => columnSet.size === unique.columns.length && unique.columns.every((columnName) => columnSet.has(columnName)),
  );
  return pkMatches || uniqueMatches;
}

/**
 * Odvozená (needitovatelná) kardinalita FK vazby (E5-US2) — 'one', pokud
 * zdrojové (referencující) sloupce nesou přesně PK nebo unique constraint
 * dané tabulky, jinak 'many'. Cílová strana je vždy PK/unique, takže z ní by
 * kardinalita vždy vyšla 'one' (epic-5 kap. 2) — počítá se proto ze
 * zdrojové/referencující strany.
 */
export function computeForeignKeyCardinality(table: TableModel, foreignKey: ForeignKeyModel): 'one' | 'many' {
  return isColumnSetKeyed(table, foreignKey.columns) ? 'one' : 'many';
}

export function describeCheckConstraintSyntaxError(expression: string): string | null {
  const trimmed = expression.trim();
  if (!trimmed) return 'empty';
  let depth = 0;
  for (const char of trimmed) {
    if (char === '(') depth += 1;
    else if (char === ')') {
      depth -= 1;
      if (depth < 0) return 'unbalanced';
    }
  }
  return depth === 0 ? null : 'unbalanced';
}
